import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCode, FiExternalLink, FiPlus, FiGlobe, FiFileText, FiMessageCircle, FiPlay, FiUsers, FiEye, FiEdit2, FiTrash2, FiCheck } from 'react-icons/fi';
import { toast } from '../../Toast';
import RemoveItemModal from '../RemoveItemModal';
import { BackLink, HubHeader, HubNote, HubTip, SearchBox, FilterChips, SelectBox, IconButton, Pager, Pill, PillTone, hubPath, useHubSite } from '../shared';

export type NewsType = 'article' | 'video' | 'podcast' | 'webinar' | 'coverage';

// type → label, list icon and pill colour; shared with the add pages (AddNews.tsx)
export const NEWS_TYPES: Record<NewsType, { label: string; plural: string; icon: React.ReactNode; tone: PillTone }> = {
    article: { label: 'Article', plural: 'Articles', icon: <FiFileText />, tone: 'cyan' },
    video: { label: 'Video', plural: 'Videos', icon: <FiPlay />, tone: 'red' },
    podcast: { label: 'Podcast', plural: 'Podcasts', icon: <FiMessageCircle />, tone: 'amber' },
    webinar: { label: 'Webinar', plural: 'Webinars', icon: <FiUsers />, tone: 'green' },
    coverage: { label: 'Media coverage', plural: 'Media coverage', icon: <FiExternalLink />, tone: 'outline' },
};

type Status = 'Draft' | 'Published' | 'Scheduled';
const STATUS_TONE: Record<Status, PillTone> = { Draft: 'grey', Published: 'green', Scheduled: 'cyan' };

interface Item {
    id: string;
    title: string;
    type: NewsType;
    // second line: "Written in Investi", or the link's site and how it opens
    source: string;
    link?: boolean;
    date: string;
    views: number | null;
    status: Status;
}

// sample items (spec 405:8855); the counts on the chips are the library totals, not this page
const ITEMS: Item[] = [
    { id: '1', title: 'Gold Mountain assay results confirm high‑grade zone', type: 'article', source: 'Written in Investi', date: '2 Sep 2026', views: 312, status: 'Draft' },
    { id: '2', title: '49 Metals on the Junior Miner podcast — Episode 214', type: 'podcast', source: 'juniorminer.example · opens in a new tab', link: true, date: '28 Aug 2026', views: 188, status: 'Published' },
    { id: '3', title: 'Sinter Project field program update', type: 'video', source: 'youtube.com · plays on the website', link: true, date: '26 Aug 2026', views: 95, status: 'Published' },
    { id: '4', title: 'September investor webinar — register now', type: 'webinar', source: 'zoom.us · registration link', link: true, date: 'Scheduled 8 Sep', views: null, status: 'Scheduled' },
    { id: '5', title: 'Buffalo Canyon drilling permits approved', type: 'article', source: 'Written in Investi', date: '14 Aug 2026', views: 640, status: 'Draft' },
    { id: '6', title: '“Nevada’s next gold story” — The Daily Ledger', type: 'coverage', source: 'dailyledger.example · opens in a new tab', link: true, date: '9 Aug 2026', views: 410, status: 'Published' },
    { id: '7', title: 'Interview: Managing Director on the FY26 strategy', type: 'video', source: 'Draft · video not added yet', date: 'Draft', views: null, status: 'Draft' },
    { id: '8', title: '49 Metals joins the Nevada Mining Association', type: 'article', source: 'Written in Investi', date: '2 Aug 2026', views: 124, status: 'Draft' },
];
const TOTALS: Record<NewsType | 'all', number> = { all: 24, article: 9, video: 5, podcast: 3, webinar: 2, coverage: 5 };
const STATUSES = ['All statuses', 'Published', 'Scheduled', 'Draft'];

// Website Hub › Website content › News & media (spec 405:8855)
const NewsMedia: React.FC = () => {
    const history = useHistory();
    const { host, short, url } = useHubSite();
    const [items, setItems] = useState(ITEMS);
    const [q, setQ] = useState('');
    const [type, setType] = useState<NewsType | 'all'>('all');
    const [status, setStatus] = useState(STATUSES[0]);
    const [removing, setRemoving] = useState<Item | null>(null);

    const shown = items.filter(i =>
        (type === 'all' || i.type === type) &&
        (status === STATUSES[0] || i.status === status) &&
        i.title.toLowerCase().includes(q.trim().toLowerCase()));

    const chips = [{ value: 'all' as const, label: 'All', count: TOTALS.all },
        ...(Object.keys(NEWS_TYPES) as NewsType[]).map(t => ({ value: t, label: NEWS_TYPES[t].plural, count: TOTALS[t] }))];

    const edit = (i: Item) => history.push(`${hubPath('content', 'news-media', 'edit')}?type=${i.type}&id=${i.id}`);

    return <>
        <BackLink to={hubPath('content')} label="Back to Website content" />
        <HubHeader title="News & Media" sub={`Articles, videos, podcasts, webinars and media coverage shown on ${host}/news-media/`}>
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/news-media')}><FiCode /> Widget Settings</button>
            <a className="btn-outline with-icon" href={url('/news-media/')} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on Website</a>
            <button type="button" className="btn-primary with-icon" onClick={() => history.push(hubPath('content', 'news-media', 'add'))}><FiPlus /> Add News or Media</button>
        </HubHeader>

        <HubNote icon={<FiGlobe />} title="Everything here becomes a WordPress post" pill="Connected">
            Publishing writes a real post on {host}, so it appears in search, in your theme and in your menus. Editing it here updates the post within a minute — you never open WordPress.
        </HubNote>

        <section className="card hub-list">
            <div className="hub-toolbar">
                <SearchBox value={q} onChange={setQ} placeholder="Search news & media" />
                <FilterChips chips={chips} value={type} onChange={setType} />
                <SelectBox label="Status" value={status} options={STATUSES} onChange={setStatus} />
            </div>

            <div className="hub-table">
                <div className="tr th"><span className="item">Item</span><span className="type">Type</span><span className="date">Date</span><span className="views">Views</span><span className="status">Status</span><span className="act" /></div>
                {shown.map(i => <div key={i.id} className="tr clickable" onClick={() => edit(i)}>
                    <span className="item">
                        <span className="thumb">{NEWS_TYPES[i.type].icon}</span>
                        <span className="t"><strong>{i.title.replace('49 Metals', short)}</strong><span>{i.link && <FiExternalLink />} {i.source}</span></span>
                    </span>
                    <span className="type"><Pill tone={NEWS_TYPES[i.type].tone}>{NEWS_TYPES[i.type].label}</Pill></span>
                    <span className="date">{i.date}</span>
                    <span className="views">{i.views === null ? '—' : i.views}</span>
                    <span className="status"><Pill tone={STATUS_TONE[i.status]}>{i.status}</Pill></span>
                    <span className="act">
                        {i.link && i.status === 'Published'
                            ? <IconButton label="Open the link" href={url('/news-media/')}><FiExternalLink /></IconButton>
                            : <IconButton label="Preview on the website" onClick={() => toast.info('Preview only: the WordPress preview isn’t connected yet')}><FiEye /></IconButton>}
                        <IconButton label="Edit" onClick={() => edit(i)}><FiEdit2 /></IconButton>
                        <IconButton label="Remove" danger onClick={() => setRemoving(i)}><FiTrash2 /></IconButton>
                    </span>
                </div>)}
                {shown.length === 0 && <p className="hub-empty">Nothing matches — try another filter.</p>}
            </div>

            <Pager shown={shown.length} total={TOTALS.all} noun="items" />
            <span className="pill-ok live"><FiCheck /> Live on the website</span>
        </section>

        <HubTip>You don't have to write anything. Paste a link to a YouTube video, podcast, webinar or news story and Investi shows it on the website with the right picture and title. ASX announcements are added automatically from Communications › Announcements.</HubTip>

        {removing && <RemoveItemModal noun={removing.type === 'article' ? 'Article' : 'Item'} name={removing.title} onClose={() => setRemoving(null)}
            onHide={() => { setItems(xs => xs.map(x => x.id === removing.id ? { ...x, status: 'Draft' } : x)); setRemoving(null); toast.info('Preview only: hidden here, the website wasn’t changed'); }}
            onRemove={() => { setItems(xs => xs.filter(x => x.id !== removing.id)); setRemoving(null); toast.info('Preview only: removed here, the website wasn’t changed'); }} />}
    </>;
};

export default NewsMedia;
